import React, { useRef, useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useUserInfo } from '../../../hooks/useUserInfo';
import { logout } from '../../../store/slices/authSlice';

interface AdminLayoutProps {
  children?: React.ReactNode;
}

interface MenuSection {
  title: string;
  items: {
    path: string;
    label: string;
    exact?: boolean;
  }[];
}

const AdminShell = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
  background: #f5f5f5;
`;

const Sidebar = styled.aside`
  display: flex;
  width: 280px;
  height: 100vh;
  flex-shrink: 0;
  flex-direction: column;
  gap: 22px;
  padding: 32px 16px 32px 32px;
  box-sizing: border-box;
  background: #f1f7f7;
  color: rgba(55, 56, 60, 0.61);
  font-size: 15px;
  text-align: left;
`;

const LogoButton = styled.button`
  display: flex;
  align-items: flex-start;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
`;

const LogoMark = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
`;

const LogoImage = styled.img`
  width: 134px;
  max-height: 100%;
`;

const SidebarScroll = styled.div`
  display: flex;
  flex: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(112, 115, 124, 0.3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(112, 115, 124, 0.5);
  }
`;

const SidebarContent = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 24px;
`;

const NavSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const NavSectionInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NavHeading = styled.div`
  display: flex;
  align-items: center;
`;

const NavHeadingText = styled.div`
  letter-spacing: 0.01em;
  line-height: 146.7%;
  font-weight: 600;
`;

const NavList = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $compact }) => ($compact ? '0' : '8px')};
  font-size: 14px;
  color: rgba(46, 47, 51, 0.88);
`;

const NavItem = styled.button<{ $active?: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  border: 0;
  border-radius: 12px;
  padding: 12px;
  background: transparent;
  color: ${({ $active }) => ($active ? '#448181' : 'rgba(46, 47, 51, 0.88)')};
  cursor: pointer;
  text-align: left;
  transition: color 0.2s ease;

  &:hover {
    color: #448181;
  }
`;

const NavItemLabel = styled.span`
  letter-spacing: 0.01em;
  line-height: 142.9%;
  font-weight: 600;
`;

const AdminMain = styled.main`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
`;

const MainContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
  padding: 32px 50px;
  background: #fff;

  @media (max-width: 1024px) {
    padding: 24px 32px;
  }

  @media (max-width: 768px) {
    padding: 20px 20px;
  }

  @media (max-width: 480px) {
    padding: 16px 16px;
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const AuthLoadingScreen = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100vh;
  background: #f1f7f7;
`;

const AuthSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid rgba(68, 129, 129, 0.2);
  border-top-color: #448181;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { userInfo, loading, error, refresh } = useUserInfo();
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const accessToken = localStorage.getItem('access_token');

  useEffect(() => {
    if (!accessToken || loading || userInfo || error) {
      return;
    }

    refresh().catch(() => {
      // The auth guard effect handles redirect/logout if recovery fails.
    });
  }, [accessToken, error, loading, refresh, userInfo]);

  useEffect(() => {
    if (!accessToken) {
      navigate('/admin/login', {
        replace: true,
        state: { from: location.pathname + location.search }
      });
      return;
    }

    if (loading) {
      return;
    }

    if (!userInfo) {
      return;
    }

    if (userInfo.role !== 'ADMIN') {
      dispatch(logout());
      navigate('/admin/login', {
        replace: true,
        state: { from: location.pathname + location.search }
      });
    }
  }, [accessToken, dispatch, loading, location.pathname, location.search, navigate, userInfo]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      scrollPositionRef.current = container.scrollTop;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    const restoreScrollPosition = () => {
      if (container && scrollPositionRef.current > 0) {
        container.scrollTop = scrollPositionRef.current;
      }
    };

    const timeoutId = setTimeout(restoreScrollPosition, 0);
    const rafId = requestAnimationFrame(restoreScrollPosition);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
    };
  }, [location.pathname]);

  const menuSections: MenuSection[] = [
    {
      title: '대시보드',
      items: [
        { path: '/admin/dashboard', label: '대시보드 요약', exact: true }
      ]
    },
    {
      title: '홈',
      items: [
        { path: '/admin/home', label: '홈 관리', exact: true },
        { path: '/admin/home/setting', label: '기본 설정' }
      ]
    },
    {
      title: '회원',
      items: [
        { path: '/admin/user', label: '회원 관리', exact: true },
        { path: '/admin/user/create', label: '회원 계정 생성' },
        { path: '/admin/user/qualification', label: '회원 자격 관리' }
      ]
    },
    {
      title: '클래스',
      items: [
        { path: '/admin/class', label: '클래스 관리', exact: true },
        { path: '/admin/class/lecture', label: '강의 관리', exact: true },
        { path: '/admin/class/lecture/register', label: '강의 등록' }
      ]
    },
    {
      title: '커피챗',
      items: [
        { path: '/admin/coffee-chat', label: '커피챗 관리', exact: true },
        { path: '/admin/coffee-chat/mentor', label: '멘토 관리' },
        { path: '/admin/coffee-chat/mentor-register', label: '멘토 등록' },
        { path: '/admin/coffee-chat/question', label: '커피챗 질문 답변 관리' }
      ]
    },
    {
      title: '커뮤니티',
      items: [
        { path: '/admin/community/post', label: '일반 게시글 관리' },
        { path: '/admin/community/admin-post', label: '관리자 게시글 관리' },
        { path: '/admin/community/lecture-qa', label: '강의 질문 답변 관리' },
        { path: '/admin/community/comment', label: '댓글 관리' },
        { path: '/admin/community/study', label: '스터디 관리' },
        { path: '/admin/community/write', label: '관리자 글쓰기' }
      ]
    },
    {
      title: '뉴스레터',
      items: [
        { path: '/admin/newsletter', label: '뉴스레터 관리', exact: true },
        { path: '/admin/newsletter/register', label: '뉴스레터 등록' }
      ]
    },
    {
      title: '1대1 문의',
      items: [
        { path: '/admin/inquiry/list', label: '받은 문의' }
      ]
    }
  ];

  const handleMenuClick = (path: string, event: React.MouseEvent<HTMLButtonElement>) => {
    if (containerRef.current) {
      scrollPositionRef.current = containerRef.current.scrollTop;
    }

    navigate(path);
    event.preventDefault();
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout());
      navigate('/admin/login', { replace: true });
    } catch (logoutError) {
      navigate('/admin/login', { replace: true });
    }
  };

  const handleLogoutConfirm = () => {
    setShowLogoutModal(false);
    handleLogout();
  };

  const isItemActive = (item: MenuSection['items'][number]) => {
    if (item.exact) {
      return location.pathname === item.path;
    }

    return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  };

  if (!accessToken || (loading && !userInfo)) {
    return (
      <AuthLoadingScreen>
        <AuthSpinner />
      </AuthLoadingScreen>
    );
  }

  if (!userInfo || userInfo.role !== 'ADMIN') {
    return null;
  }

  return (
    <AdminShell>
      <Sidebar>
        <LogoButton type="button" onClick={() => navigate('/')}>
          <LogoMark>
            <LogoImage alt="To Be Doctor Campus" src="/logo.svg" />
          </LogoMark>
        </LogoButton>

        <SidebarScroll>
          <SidebarContent>
            {menuSections.map((section) => (
              <NavSection key={section.title}>
                <NavSectionInner>
                  <NavHeading>
                    <NavHeadingText>{section.title}</NavHeadingText>
                  </NavHeading>

                  <NavList $compact={section.title === '대시보드' || section.title === '1대1 문의'}>
                    {section.items.map((item) => (
                      <NavItem
                        key={item.path}
                        type="button"
                        $active={isItemActive(item)}
                        onClick={(event) => handleMenuClick(item.path, event)}
                      >
                        <NavItemLabel>{item.label}</NavItemLabel>
                      </NavItem>
                    ))}
                  </NavList>
                </NavSectionInner>
              </NavSection>
            ))}

            <NavSection>
              <NavSectionInner>
                <NavHeading>
                  <NavHeadingText>계정</NavHeadingText>
                </NavHeading>

                <NavList>
                  <LogoutItem type="button" onClick={() => setShowLogoutModal(true)}>
                    <NavItemLabel>로그아웃</NavItemLabel>
                  </LogoutItem>
                </NavList>
              </NavSectionInner>
            </NavSection>
          </SidebarContent>
        </SidebarScroll>
      </Sidebar>

      <AdminMain>
        <MainContent ref={containerRef}>{children || <Outlet />}</MainContent>
      </AdminMain>

      {showLogoutModal && (
        <ModalBackdrop onClick={() => setShowLogoutModal(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>로그아웃</ModalTitle>
            <ModalBody>로그아웃하시겠습니까?</ModalBody>
            <ModalActions>
              <ModalCancel type="button" onClick={() => setShowLogoutModal(false)}>
                취소
              </ModalCancel>
              <ModalConfirm type="button" onClick={handleLogoutConfirm}>
                로그아웃
              </ModalConfirm>
            </ModalActions>
          </ModalBox>
        </ModalBackdrop>
      )}
    </AdminShell>
  );
};

const LogoutItem = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  border: 0;
  border-radius: 12px;
  padding: 12px;
  background: transparent;
  color: #e52222;
  cursor: pointer;
  text-align: left;
  transition: color 0.2s ease;

  &:hover {
    color: #c41c1c;
  }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
`;

const ModalBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 320px;
  border-radius: 16px;
  background: #fff;
  padding: 28px 24px 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #171719;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.4;
`;

const ModalBody = styled.p`
  margin: 0;
  color: rgba(55, 56, 60, 0.88);
  font-size: 15px;
  line-height: 1.5;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
`;

const ModalCancel = styled.button`
  border-radius: 10px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  background: #fff;
  padding: 10px 20px;
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: rgba(112, 115, 124, 0.4);
  }
`;

const ModalConfirm = styled.button`
  border-radius: 10px;
  background: #e52222;
  padding: 10px 20px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #c41c1c;
  }
`;

export default AdminLayout;
